import React from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "../theme";
import { Card, Display, Pill, PushButton, Squiggle } from "../ui";
import { formatDate, statusOf } from "../cohortStatus";
import { ClassroomScene } from "../illustrations/ClassroomScene";

const PROOF = ["Live mentor-led classes", "Capped batches", "Full syllabus up front"];

// Brand-level, not cohort-level: these have to stay true as new cohorts of
// different lengths and sizes are added.
const STATS = [
  { big: "Live", small: "Taught, not pre-recorded", bg: rf.amber },
  { big: "Together", small: "One batch, one pace", bg: rf.coral },
  { big: "Capped", small: "Seats close, then it starts", bg: rf.sky },
];

export const Hero = ({ leadCohort, loading, onSeeCohorts, onTalk }) => {
  const reduced = useReducedMotion();
  const status = leadCohort ? statusOf(leadCohort) : null;

  return (
    <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
          <Grid item xs={12} md={7}>
            <motion.div variants={stagger(0.08)} {...revealProps(reduced)}>
              <motion.div variants={fadeUp}>
                <Pill bg={rf.paper} sx={{ mb: 3 }}>
                  ✦ Cohort-based · live · small batches
                </Pill>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Display component="h1" size={62} sx={{ mb: 2.5 }}>
                  Serious cohorts
                  <br />
                  for working engineers.
                </Display>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Typography sx={{ fontFamily: rf.body, fontSize: { xs: 16.5, md: 18.5 }, lineHeight: 1.6, color: rf.inkSoft, maxWidth: 560, mb: 3.5 }}>
                  Mentor-led cohorts in AI engineering, data science and more — built for working professionals. A
                  fixed syllabus, weekly assignments reviewed by the instructor who taught the class, and projects you
                  ship yourself.
                </Typography>
              </motion.div>

              <motion.div variants={fadeUp}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75, mb: 3 }}>
                  <PushButton variant="primary" size="lg" onClick={onSeeCohorts}>
                    See running cohorts
                  </PushButton>
                  <PushButton variant="light" size="lg" onClick={onTalk}>
                    Ask before you commit
                  </PushButton>
                </Box>
              </motion.div>

              {leadCohort && !loading && (
                <motion.div variants={fadeUp}>
                  <Card bg={rf.paper} shadow={rf.shadowSm} sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, px: 2, py: 1.1, borderRadius: rf.radiusPill, mb: 3 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: rf.mint, border: rf.lineThin }} />
                    <Typography sx={{ fontFamily: rf.display, fontSize: 14.5, fontWeight: 700, color: rf.ink }}>
                      {leadCohort.title}
                    </Typography>
                    <Typography sx={{ fontFamily: rf.body, fontSize: 14, color: rf.inkSoft }}>
                      {status?.label} · starts {formatDate(leadCohort.start_date)}
                    </Typography>
                  </Card>
                </motion.div>
              )}

              <motion.div variants={fadeUp}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {PROOF.map((line) => (
                    <Pill key={line} bg={rf.creamDeep} sx={{ fontWeight: 600 }}>
                      {line}
                    </Pill>
                  ))}
                </Box>
              </motion.div>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={5}>
            <motion.div variants={fadeUp} {...revealProps(reduced)}>
              <ClassroomScene />
            </motion.div>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center", my: { xs: 4, md: 5 } }}>
          <Squiggle color={rf.ink} width={140} />
        </Box>

        {/* The flat stat band the reference site uses to punctuate its hero. */}
        <motion.div variants={fadeUp} {...revealProps(reduced)}>
          <Card bg={rf.amber} shadow={rf.shadowLg} sx={{ overflow: "hidden" }}>
            <Grid container>
              {STATS.map((stat, i) => (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  key={stat.big}
                  sx={{
                    p: { xs: 2.5, md: 3.5 },
                    textAlign: "center",
                    backgroundColor: stat.bg,
                    borderRight: { sm: i < STATS.length - 1 ? rf.line : "none" },
                    borderBottom: { xs: i < STATS.length - 1 ? rf.line : "none", sm: "none" },
                  }}
                >
                  <Display component="p" size={34} sx={{ mb: 0.5 }}>
                    {stat.big}
                  </Display>
                  <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, fontWeight: 600, color: rf.ink }}>
                    {stat.small}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Hero;
